// A label's size, as every label size picker reads and shows it (Mitch, 2026-11-04: "every label size picker uses the
// same exact shared module code"): width, height and unit (mm, the default, or inches), a size kept in mm to 0.1 within
// what a label can be (10 to 400 mm a side), the presets (the size Hand Held Legend set first, then the common stock),
// and Rotate (width and height swapped). Its users: ui/LabelSizeFields.tsx (the vendor portal and a label part's size)
// and the vendors' documents page (server/src/po/docs-page.ts, plain HTML, whose small script does this same math).
export type MmSize = { w: number; h: number };
export type LabelUnit = "mm" | "in";
export type LabelPreset = MmSize & { label: string };

/**
 * Common label stock for the size picker, widest roll first: thermal labels from 4 × 6 in shipping labels down to 2 × 1 in
 * item labels, the metric sizes Chinese printers stock, and squares. One entry per stock, the way it usually comes off
 * the roll; the picker's Rotate button makes it tall or wide (`labelPreset()` finds a stock either way). Any size from 10
 * to 400 mm will do too.
 */
export const LABEL_SIZES: readonly { w: number; h: number; label: string }[] = [
  { w: 100, h: 150, label: "100 × 150 mm (4 × 6 in)" },
  { w: 100, h: 100, label: "100 × 100 mm square (4 × 4 in)" },
  { w: 100, h: 50, label: "100 × 50 mm (4 × 2 in)" },
  { w: 76, h: 76, label: "76 × 76 mm square (3 × 3 in)" },
  { w: 76, h: 51, label: "76 × 51 mm (3 × 2 in)" },
  { w: 60, h: 40, label: "60 × 40 mm" },
  { w: 57, h: 32, label: "57 × 32 mm (2.25 × 1.25 in)" },
  { w: 51, h: 25, label: "51 × 25 mm (2 × 1 in)" },
  { w: 50, h: 50, label: "50 × 50 mm square (2 × 2 in)" },
  { w: 50, h: 30, label: "50 × 30 mm" },
  { w: 40, h: 30, label: "40 × 30 mm" },
  { w: 25, h: 25, label: "25 × 25 mm square (1 × 1 in)" },
];

export const LABEL_MIN_MM = 10;
export const LABEL_MAX_MM = 400;
export const MM_PER_IN = 25.4;

/** A size to 0.1 mm. */
export const roundMm = (mm: number) => Math.round(mm * 10) / 10;

/** A typed side in mm, when it's a size a label can be (null otherwise: empty, not a number, off the limits). */
export function toMm(text: string, unit: LabelUnit): number | null {
  if (!text.trim()) return null;
  const n = Number(text.replace(",", ".").trim()) * (unit === "in" ? MM_PER_IN : 1);
  return Number.isFinite(n) && n >= LABEL_MIN_MM && n <= LABEL_MAX_MM ? roundMm(n) : null;
}

/** A side in mm as its field shows it: mm to 0.1, inches to 0.01. */
export const fromMm = (mm: number | null | undefined, unit: LabelUnit) =>
  mm == null ? "" : unit === "in" ? String(Math.round((mm / MM_PER_IN) * 100) / 100) : String(roundMm(mm));

/** The dropdown's value for a size: its preset's, else "custom". */
export const presetKey = (s: MmSize) => `${s.w}x${s.h}`;

/** The same size either way round. */
export const sameSize = (a: MmSize, b: MmSize) => (a.w === b.w && a.h === b.h) || (a.w === b.h && a.h === b.w);

/** Width and height swapped. */
export const rotated = (s: MmSize): MmSize => ({ w: s.h, h: s.w });

/** The presets for a label: the size Hand Held Legend set first ("76 × 51 mm · set by Hand Held Legend"), then the common stock. */
export function labelPresets(set: MmSize | null, setBy: string, sizeText: (s: MmSize) => string): LabelPreset[] {
  return [...(set ? [{ ...set, label: `${sizeText(set)} · ${setBy}` }] : []), ...LABEL_SIZES.filter((s) => !set || !sameSize(s, set))];
}
